import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  TablePagination,
  Tooltip,
  Typography
} from "@mui/material";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import RestoreRoundedIcon from "@mui/icons-material/RestoreRounded";

import { ActivityEvent, useActivityEvents } from "../api/hooks";
import { extractApiError } from "../api/client";
import { useSettingsStore } from "../store/settingsStore";

type NotificationSeverity = "error" | "warning" | "info" | "success";

const STORAGE_KEY = "pec-notifications-dismissed";

function classifySeverity(event: ActivityEvent): NotificationSeverity {
  const type = (event.type || "").toLowerCase();
  const status = (event.status || "").toLowerCase();

  if (type.includes("failed") || status === "failed" || event.error) {
    return "error";
  }
  if (type.includes("completed")) {
    return "success";
  }
  if (status === "pending" || status === "running") {
    return "warning";
  }
  return "info";
}

function iconForSeverity(severity: NotificationSeverity) {
  const commonProps = { fontSize: "small" as const };
  switch (severity) {
    case "error":
      return <ErrorOutlineRoundedIcon {...commonProps} />;
    case "warning":
      return <WarningAmberRoundedIcon {...commonProps} />;
    case "success":
      return <CheckCircleRoundedIcon {...commonProps} />;
    default:
      return <InfoRoundedIcon {...commonProps} />;
  }
}

function formatWhen(timestamp: string) {
  return new Date(timestamp).toLocaleString();
}

function eventKey(event: ActivityEvent) {
  return `${event.type}-${event.timestamp}-${event.account ?? ""}-${event.target_date ?? ""}`;
}

function describe(event: ActivityEvent) {
  const parts: string[] = [];
  if (event.account) {
    parts.push(`Casella ${event.account}`);
  }
  if (event.target_date) {
    parts.push(`Data ${event.target_date}`);
  }
  if (event.status) {
    parts.push(`Stato ${event.status}`);
  }
  if (event.error) {
    parts.push(`Errore: ${event.error}`);
  }
  return parts.join(" · ");
}

function titleFor(event: ActivityEvent) {
  const type = (event.type || "").replace("_", " ");
  if (type === "job failed") return "Job fallito";
  if (type === "job completed") return "Job completato";
  if (type === "job started") return "Job avviato";
  if (type === "account progress") return "Avanzamento casella";
  return type || "Notifica";
}

export function NotificationsCenter() {
  const activityRefreshMs = useSettingsStore((state) => state.activityRefreshMs);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filter, setFilter] = useState<NotificationSeverity | "all">("all");
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        setDismissed(new Set(JSON.parse(raw)));
      }
    } catch {
      setDismissed(new Set());
    }
  }, []);

  const persistDismissed = (next: Set<string>) => {
    setDismissed(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(next)));
    } catch {
      /* ignore persistence errors */
    }
  };

  const { data, isLoading, isFetching, isError, error, refetch } = useActivityEvents(300, activityRefreshMs, true);
  const events = data?.events ?? [];
  const ordered = useMemo(
    () => [...events].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()),
    [events]
  );

  const filtered = useMemo(() => {
    return ordered.filter((event) => {
      const key = eventKey(event);
      if (dismissed.has(key)) {
        return false;
      }
      const severity = classifySeverity(event);
      return filter === "all" || severity === filter;
    });
  }, [ordered, dismissed, filter]);

  const paged = useMemo(() => {
    const start = page * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, page, rowsPerPage]);

  const unreadCount = ordered.filter((event) => !dismissed.has(eventKey(event))).length;

  const handleDismiss = (key: string) => {
    const next = new Set(dismissed);
    next.add(key);
    persistDismissed(next);
  };

  const handleMarkAllRead = () => {
    const next = new Set(dismissed);
    ordered.forEach((event) => next.add(eventKey(event)));
    persistDismissed(next);
  };

  const handleReset = () => {
    persistDismissed(new Set());
    setPage(0);
  };

  return (
    <Card>
      <CardHeader
        title="Notifiche"
        subheader="Messaggi di sistema generati dai job e dall'archiviazione"
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label={`Non lette: ${unreadCount}`} color={unreadCount > 0 ? "primary" : "default"} size="small" />
            <Tooltip title="Aggiorna">
              <IconButton onClick={() => refetch()} disabled={isFetching}>
                {isFetching ? <CircularProgress size={18} /> : <RefreshRoundedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Stack>
        }
      />
      <Divider />
      <CardContent sx={{ pt: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Stack direction="row" spacing={1}>
            {[
              { value: "all" as const, label: "Tutte" },
              { value: "error" as const, label: "Errori" },
              { value: "warning" as const, label: "Avvisi" },
              { value: "info" as const, label: "Info" },
              { value: "success" as const, label: "Completate" }
            ].map((option) => (
              <Chip
                key={option.value}
                label={option.label}
                color={filter === option.value ? "primary" : "default"}
                variant={filter === option.value ? "filled" : "outlined"}
                onClick={() => {
                  setFilter(option.value);
                  setPage(0);
                }}
              />
            ))}
          </Stack>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              size="small"
              variant="outlined"
              startIcon={<DoneAllRoundedIcon />}
              onClick={handleMarkAllRead}
              disabled={ordered.length === 0}
            >
              Segna tutto letto
            </Button>
            <Button
              size="small"
              variant="text"
              startIcon={<RestoreRoundedIcon />}
              onClick={handleReset}
              disabled={dismissed.size === 0}
            >
              Ripristina elenco
            </Button>
          </Stack>
        </Stack>

        {isLoading ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Caricamento notifiche...
            </Typography>
          </Stack>
        ) : isError ? (
          <Alert severity="error">Impossibile recuperare le notifiche: {extractApiError(error)}</Alert>
        ) : paged.length === 0 ? (
          <Alert severity="info">Nessuna notifica disponibile.</Alert>
        ) : (
          <>
            <List>
              {paged.map((event) => {
                const severity = classifySeverity(event);
                const key = eventKey(event);
                return (
                  <ListItem
                    key={key}
                    alignItems="flex-start"
                    secondaryAction={
                      <Tooltip title="Rimuovi dalla lista">
                        <IconButton edge="end" onClick={() => handleDismiss(key)}>
                          <DeleteRoundedIcon />
                        </IconButton>
                      </Tooltip>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: `${severity}.main` }}>
                        {iconForSeverity(severity)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                          <Typography variant="subtitle1">{titleFor(event)}</Typography>
                          <Chip label={severity.toUpperCase()} size="small" color={severity === "info" ? "default" : severity} />
                          <Typography variant="caption" color="text.secondary">
                            {formatWhen(event.timestamp)}
                          </Typography>
                        </Stack>
                      }
                      secondary={
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">
                            {describe(event) || "Dettagli non disponibili"}
                          </Typography>
                          {event.total_messages !== undefined ? (
                            <Typography variant="body2" color="text.secondary">
                              Messaggi coinvolti: {event.total_messages}
                            </Typography>
                          ) : null}
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Notifiche per pagina"
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}
